/**
 * /erp/dispatches/{dispatchId} — dispatch detail (Manager+ for PR-F).
 *
 * Layout:
 *
 *   * Header — code + status badge + lifecycle buttons appropriate
 *     for the current status (draft → confirm or delete; confirmed →
 *     in-transit / cancel; in_transit → deliver / cancel; delivered →
 *     cancel; cancelled → no-ops).
 *   * Summary — driver/transport/CRM-link/notes/timestamps. Editable
 *     header fields open in an inline dialog.
 *   * Deliveries — read-only table of deliveries + lines. While the
 *     dispatch is still ``draft`` an "Edit deliveries" button takes
 *     the manager to the dedicated full-screen editor; once confirmed,
 *     the lines freeze for audit-trail integrity.
 *
 * Lifecycle buttons surface API errors prominently (negative-stock
 * blocks at confirm/cancel-from-delivered, missing destinations,
 * status mismatches). Each button asks for confirmation before firing
 * — these transitions move inventory and are auditable, so a stray
 * click is more disruptive than a list/CRUD action.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  Ban,
  Box,
  Check,
  Edit,
  KeyRound,
  ListChecks,
  Loader2,
  Pencil,
  PlayCircle,
  Printer,
  ShipWheel,
  ShoppingCart,
  Trash2,
  Truck,
  Warehouse,
  Wrench,
  X,
} from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";

import {
  ApiError,
  type ErpDispatchRead,
  type ErpDispatchUpdate,
  accountsApi,
  contactsApi,
  dealsApi,
  erpApi,
  isErpDispatchConflict,
} from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { useDispatch, useDrivers, useOrder, useTransports } from "../lib/erp";
import { toast } from "../lib/toast";
import { formatQty } from "../lib/units";
import { CustomFieldsPanel } from "../components/custom-fields/CustomFieldsPanel";
import { StatusBadge } from "./_app.erp.dispatches.index";

export const Route = createFileRoute("/_app/erp/dispatches/$dispatchId")({
  component: DispatchDetailPage,
});

type Pending =
  | { kind: "confirm" }
  | { kind: "in_transit" }
  | { kind: "deliver" }
  | { kind: "cancel" }
  | { kind: "delete" }
  | { kind: "edit-header" }
  | null;

function DispatchDetailPage() {
  const { dispatchId } = useParams({ from: "/_app/erp/dispatches/$dispatchId" });
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // Manager+ controls the lifecycle (confirm / in-transit / deliver /
  // cancel / delete) per PRD Q13. Sales can still create + edit drafts
  // — those buttons stay visible regardless of role.
  const isManagerPlus = user?.role === "admin" || user?.role === "manager";
  const navigate = useNavigate();
  const dispatchQ = useDispatch(dispatchId);
  const [pending, setPending] = useState<Pending>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const onMutationError = (err: unknown) => {
    if (err instanceof ApiError) {
      const detail = err.detail;
      if (isErpDispatchConflict(detail)) {
        let msg = detail.message;
        if (detail.code === "inventory_negative_blocked") {
          const blocked = detail.blocked
            .map((b) => `${b.item_kind} ${b.item_id.slice(0, 8)}…`)
            .join(", ");
          msg = `${msg} Blocked items: ${blocked}.`;
        }
        setActionError(msg);
        return;
      }
    }
    const msg = err instanceof ApiError ? err.message : "Action failed.";
    setActionError(msg);
    toast.error(msg);
  };

  const onLifecycleSuccess = (label: string) => (d: ErpDispatchRead) => {
    queryClient.invalidateQueries({ queryKey: ["erp", "dispatches"] });
    queryClient.invalidateQueries({ queryKey: ["erp", "inventory"] });
    toast.success(`${d.code}: ${label}.`);
    setPending(null);
    setActionError(null);
  };

  const confirmMutation = useMutation({
    mutationFn: () => erpApi.dispatches.confirm(dispatchId),
    onSuccess: onLifecycleSuccess("confirmed"),
    onError: onMutationError,
  });
  const inTransitMutation = useMutation({
    mutationFn: () => erpApi.dispatches.inTransit(dispatchId),
    onSuccess: onLifecycleSuccess("marked in transit"),
    onError: onMutationError,
  });
  const deliverMutation = useMutation({
    mutationFn: () => erpApi.dispatches.deliver(dispatchId),
    onSuccess: onLifecycleSuccess("delivered"),
    onError: onMutationError,
  });
  const cancelMutation = useMutation({
    mutationFn: (reason: string) =>
      erpApi.dispatches.cancel(dispatchId, reason ? { reason } : undefined),
    onSuccess: onLifecycleSuccess("cancelled"),
    onError: onMutationError,
  });
  const deleteMutation = useMutation({
    mutationFn: () => erpApi.dispatches.remove(dispatchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["erp", "dispatches"] });
      toast.success("Dispatch deleted.");
      navigate({ to: "/erp/dispatches" });
    },
    onError: onMutationError,
  });

  if (dispatchQ.isLoading) {
    return (
      <div className="page-shell p-4 sm:p-6">
        <p className="flex items-center gap-2 text-sm opacity-60">
          <Loader2 className="size-4 animate-spin" aria-hidden /> Loading dispatch…
        </p>
      </div>
    );
  }
  if (dispatchQ.error) {
    return (
      <div className="page-shell p-4 sm:p-6">
        <Link
          to="/erp/dispatches"
          className="inline-flex items-center gap-1 text-xs opacity-70 hover:underline"
        >
          <ArrowLeft className="size-3" aria-hidden /> Dispatches
        </Link>
        <p className="mt-3 text-sm text-red-700 dark:text-red-300">
          Could not load dispatch: {(dispatchQ.error as Error).message}
        </p>
      </div>
    );
  }
  const d = dispatchQ.data;
  if (!d) return null;

  const canConfirm = isManagerPlus && d.status === "draft";
  const canInTransit = isManagerPlus && d.status === "confirmed";
  const canDeliver = isManagerPlus && d.status === "in_transit";
  const canCancel = isManagerPlus && d.status !== "cancelled";
  const canDelete = isManagerPlus && d.status === "draft";
  const canEditHeader = d.status !== "cancelled";
  const canEditDeliveries = d.status === "draft";

  return (
    <div className="page-shell space-y-5 p-4 sm:p-6">
      <Link
        to="/erp/dispatches"
        className="inline-flex items-center gap-1 text-xs opacity-70 hover:underline"
      >
        <ArrowLeft className="size-3" aria-hidden /> Dispatches
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Truck className="size-5 opacity-70" aria-hidden />
            <span className="rounded-md bg-black/5 px-2 py-0.5 font-mono text-base dark:bg-white/10">
              {d.code}
            </span>
            <StatusBadge status={d.status} />
          </h1>
          <p className="text-sm opacity-60">
            {d.deliveries.length} {d.deliveries.length === 1 ? "delivery" : "deliveries"}
            {d.dispatch_total ? <> · total {d.dispatch_total}</> : null}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {canEditHeader && (
            <button
              type="button"
              onClick={() => setPending({ kind: "edit-header" })}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
            >
              <Pencil className="size-4" aria-hidden /> Edit header
            </button>
          )}
          {canEditDeliveries && (
            <Link
              to="/erp/dispatches/$dispatchId/deliveries"
              params={{ dispatchId: d.id }}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
            >
              <ListChecks className="size-4" aria-hidden /> Edit deliveries
            </Link>
          )}
          {canConfirm && (
            <button
              type="button"
              onClick={() => setPending({ kind: "confirm" })}
              className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500"
            >
              <Check className="size-4" aria-hidden /> Confirm
            </button>
          )}
          {canInTransit && (
            <button
              type="button"
              onClick={() => setPending({ kind: "in_transit" })}
              className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-500"
            >
              <PlayCircle className="size-4" aria-hidden /> Mark in transit
            </button>
          )}
          {canDeliver && (
            <button
              type="button"
              onClick={() => setPending({ kind: "deliver" })}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-500"
            >
              <ShipWheel className="size-4" aria-hidden /> Mark delivered
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              onClick={() => setPending({ kind: "cancel" })}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40 dark:hover:text-red-300"
            >
              <Ban className="size-4" aria-hidden /> Cancel
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={() => setPending({ kind: "delete" })}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40 dark:hover:text-red-300"
            >
              <Trash2 className="size-4" aria-hidden /> Delete
            </button>
          )}
          {/* Print buttons (PR-H). The field copy is the
              anonymized slip the driver carries; the key sheet is
              the manager-only inverse map. Both open in a new tab
              — they're full HTML pages served straight by the API,
              so window.open is the right primitive (no fetch needed,
              no client-side rendering). The browser's existing
              session cookie comes along for free. */}
          <a
            href={`/api/erp/dispatches/${d.id}/print/field`}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
          >
            <Printer className="size-4" aria-hidden /> Print field copy
          </a>
          {isManagerPlus && (
            <a
              href={`/api/erp/dispatches/${d.id}/print/key`}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
            >
              <KeyRound className="size-4" aria-hidden /> Print key sheet
            </a>
          )}
        </div>
      </header>

      {actionError && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-200">
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <div className="flex-1">
            <p className="font-medium">Action blocked</p>
            <p className="mt-0.5 text-xs">{actionError}</p>
          </div>
          <button
            type="button"
            onClick={() => setActionError(null)}
            aria-label="Dismiss"
            className="rounded p-0.5 hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
      )}

      <SummaryGrid d={d} />
      <DeliveriesPanel d={d} />

      {pending?.kind === "confirm" && (
        <ConfirmDialog
          title={`Confirm ${d.code}?`}
          body={
            <div className="space-y-2 text-sm opacity-80">
              <p>
                Confirming locks the line composition and{" "}
                <span className="font-medium">debits the source warehouses</span> for every line. If
                a source is already at qty &lt; 0 for one of the items, the confirm is hard-blocked.
              </p>
              <p>
                After confirmation, you can edit driver / transport / notes / CRM links, but lines,
                recipients, and source warehouses freeze. To change them: cancel and create a new
                dispatch.
              </p>
            </div>
          }
          confirmLabel="Confirm dispatch"
          confirmColor="blue"
          onCancel={() => setPending(null)}
          onConfirm={() => confirmMutation.mutate()}
          loading={confirmMutation.isPending}
        />
      )}

      {pending?.kind === "in_transit" && (
        <ConfirmDialog
          title={`Mark ${d.code} in transit?`}
          body={
            <p className="text-sm opacity-80">
              Records that the dispatch has left the source. No inventory motion. Use this when the
              driver has actually departed (it's the trigger for the field-print key sheet workflow
              in PR-H).
            </p>
          }
          confirmLabel="Mark in transit"
          confirmColor="amber"
          onCancel={() => setPending(null)}
          onConfirm={() => inTransitMutation.mutate()}
          loading={inTransitMutation.isPending}
        />
      )}

      {pending?.kind === "deliver" && (
        <ConfirmDialog
          title={`Mark ${d.code} delivered?`}
          body={
            <div className="space-y-2 text-sm opacity-80">
              <p>
                Records the dispatch as completed at the destination(s). Internal deliveries credit
                their destination warehouses; external deliveries don't move stock at this step (the
                source debit happened at confirm).
              </p>
              <p>You can still cancel later, which reverses all motions including the credits.</p>
            </div>
          }
          confirmLabel="Mark delivered"
          confirmColor="emerald"
          onCancel={() => setPending(null)}
          onConfirm={() => deliverMutation.mutate()}
          loading={deliverMutation.isPending}
        />
      )}

      {pending?.kind === "cancel" && (
        <CancelDialog
          dispatch={d}
          onCancel={() => setPending(null)}
          onConfirm={(reason) => cancelMutation.mutate(reason)}
          loading={cancelMutation.isPending}
        />
      )}

      <CustomFieldsPanel entity="dispatches" recordKey={d.id} />

      {pending?.kind === "delete" && (
        <ConfirmDialog
          title={`Delete ${d.code}?`}
          body={
            <p className="text-sm opacity-80">
              Only draft dispatches can be deleted. This is permanent — the dispatch row and any
              audit entries we've written go away. If it's been confirmed, cancel instead so the
              motion is reversed and the history is preserved.
            </p>
          }
          confirmLabel="Delete dispatch"
          confirmColor="red"
          onCancel={() => setPending(null)}
          onConfirm={() => deleteMutation.mutate()}
          loading={deleteMutation.isPending}
        />
      )}

      {pending?.kind === "edit-header" && (
        <EditHeaderDialog
          dispatch={d}
          onClose={() => setPending(null)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ["erp", "dispatches"] });
            setPending(null);
          }}
          onError={onMutationError}
        />
      )}
    </div>
  );
}

function SummaryGrid({ d }: { d: ErpDispatchRead }) {
  const driver =
    d.driver_name && d.driver_code ? `${d.driver_name} (${d.driver_code})` : d.driver_name;
  const transport =
    d.transport_vehicle_number && d.transport_code
      ? `${d.transport_vehicle_number} (${d.transport_code})`
      : d.transport_vehicle_number;

  const rows: { label: string; value: React.ReactNode }[] = [];
  if (driver || d.temp_driver_name) {
    rows.push({
      label: "Driver",
      value: (
        <span>
          {driver}
          {!driver && d.temp_driver_name && (
            <>
              <span>{d.temp_driver_name}</span>
              <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                one-off
              </span>
            </>
          )}
        </span>
      ),
    });
  }
  if (transport || d.temp_transport_name) {
    rows.push({
      label: "Transport",
      value: (
        <span>
          {transport}
          {!transport && d.temp_transport_name && (
            <>
              <span>{d.temp_transport_name}</span>
              <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                one-off
              </span>
            </>
          )}
        </span>
      ),
    });
  }
  if (d.deal_title) {
    rows.push({
      label: "Linked deal",
      value: (
        <Link
          to="/deals/$dealId"
          params={{ dealId: d.deal_id! }}
          className="text-[var(--accent)] hover:underline"
        >
          {d.deal_title}
        </Link>
      ),
    });
  }
  if (d.account_name) {
    rows.push({
      label: "Linked account",
      value: (
        <Link
          to="/accounts/$accountId"
          params={{ accountId: d.account_id! }}
          className="text-[var(--accent)] hover:underline"
        >
          {d.account_name}
        </Link>
      ),
    });
  }
  if (d.contact_name) {
    rows.push({
      label: "Linked contact",
      value: (
        <Link
          to="/contacts/$contactId"
          params={{ contactId: d.contact_id! }}
          className="text-[var(--accent)] hover:underline"
        >
          {d.contact_name}
        </Link>
      ),
    });
  }
  if (d.notes) {
    rows.push({
      label: "Notes",
      value: <span className="whitespace-pre-wrap opacity-90">{d.notes}</span>,
    });
  }
  rows.push({
    label: "Created",
    value: (
      <span>
        {new Date(d.created_at).toLocaleString()}
        {d.created_by_name && <span className="ml-1 opacity-70">by {d.created_by_name}</span>}
      </span>
    ),
  });
  if (d.confirmed_at) {
    rows.push({
      label: "Confirmed",
      value: <span>{new Date(d.confirmed_at).toLocaleString()}</span>,
    });
  }
  if (d.in_transit_at) {
    rows.push({
      label: "In transit",
      value: <span>{new Date(d.in_transit_at).toLocaleString()}</span>,
    });
  }
  if (d.delivered_at) {
    rows.push({
      label: "Delivered",
      value: <span>{new Date(d.delivered_at).toLocaleString()}</span>,
    });
  }
  if (d.cancelled_at) {
    rows.push({
      label: "Cancelled",
      value: <span>{new Date(d.cancelled_at).toLocaleString()}</span>,
    });
  }
  if (d.cancel_reason) {
    rows.push({
      label: "Cancel reason",
      value: <span className="opacity-90">{d.cancel_reason}</span>,
    });
  }

  if (rows.length === 0) {
    return null;
  }
  return (
    <div className="rounded-xl border border-[var(--border)]">
      <dl className="divide-y divide-[var(--border)] text-sm">
        {rows.map((r) => (
          <div key={r.label} className="grid grid-cols-[max-content_1fr] gap-3 px-4 py-2">
            <dt className="text-xs uppercase tracking-wider opacity-60">{r.label}</dt>
            <dd>{r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function DeliveriesPanel({ d }: { d: ErpDispatchRead }) {
  return (
    <section className="space-y-2 rounded-xl border border-[var(--border)] p-4">
      <header className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">
          Deliveries <span className="ml-1 opacity-60">({d.deliveries.length})</span>
        </h2>
        {d.status === "draft" && (
          <Link
            to="/erp/dispatches/$dispatchId/deliveries"
            params={{ dispatchId: d.id }}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/5"
          >
            <Pencil className="size-3" aria-hidden /> Edit
          </Link>
        )}
      </header>
      {d.deliveries.length === 0 ? (
        <p className="rounded-md border border-dashed border-[var(--border)] p-4 text-xs opacity-60">
          No deliveries yet. Add at least one before confirming.
        </p>
      ) : (
        <div className="space-y-3">
          {d.deliveries.map((delivery) => (
            <article
              key={delivery.id}
              className="rounded-md border border-[var(--border)]"
              aria-labelledby={`delivery-${delivery.id}-heading`}
            >
              <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--border)] bg-black/[0.02] px-3 py-1.5 text-xs dark:bg-white/[0.02]">
                <div id={`delivery-${delivery.id}-heading`} className="flex items-center gap-2">
                  <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono dark:bg-white/10">
                    #{delivery.position + 1}
                  </span>
                  <KindPill kind={delivery.kind} />
                  <span className="opacity-60">·</span>
                  <span className="flex items-center gap-1">
                    <Warehouse className="size-3 opacity-60" aria-hidden />
                    <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono dark:bg-white/10">
                      {delivery.source_warehouse_code}
                    </span>
                  </span>
                  {delivery.kind === "internal" && delivery.dest_warehouse_code && (
                    <>
                      <span className="opacity-60">→</span>
                      <span className="flex items-center gap-1">
                        <Warehouse className="size-3 opacity-60" aria-hidden />
                        <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono dark:bg-white/10">
                          {delivery.dest_warehouse_code}
                        </span>
                      </span>
                    </>
                  )}
                  {delivery.kind === "external" && (
                    <>
                      <span className="opacity-60">→</span>
                      <span className="opacity-90">
                        {delivery.account_name || delivery.contact_name || "—"}
                        {delivery.account_name && delivery.contact_name && (
                          <span className="ml-1 opacity-60">/ {delivery.contact_name}</span>
                        )}
                      </span>
                    </>
                  )}
                </div>
                {delivery.delivery_total && (
                  <span className="font-mono opacity-80">{delivery.delivery_total}</span>
                )}
              </header>
              {delivery.kind === "external" && delivery.resolved_address && (
                <p className="px-3 pt-1 text-xs opacity-70">
                  <span className="opacity-60">Ship to:</span> {delivery.resolved_address}
                  {delivery.delivery_address && (
                    <span className="ml-1 rounded bg-black/5 px-1 text-[10px] dark:bg-white/10">
                      override
                    </span>
                  )}
                </p>
              )}
              {delivery.lines.length === 0 ? (
                <p className="px-3 py-2 text-xs opacity-60">No lines.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b border-[var(--border)] text-left text-[11px] uppercase tracking-wider opacity-60">
                    <tr>
                      <th className="px-3 py-1.5">Item</th>
                      <th className="px-3 py-1.5 text-right">Qty</th>
                      {delivery.kind === "external" && (
                        <>
                          <th className="px-3 py-1.5 text-right">Unit price</th>
                          <th className="px-3 py-1.5 text-right">Line total</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {delivery.lines.map((ln) => (
                      <tr key={ln.id} className="border-b border-[var(--border)] last:border-0">
                        <td className="px-3 py-1.5">
                          <div className="flex items-center gap-2">
                            {ln.item_kind === "product" ? (
                              <Box className="size-3 opacity-60" aria-hidden />
                            ) : (
                              <Wrench className="size-3 opacity-60" aria-hidden />
                            )}
                            <span className="rounded-md bg-black/5 px-1.5 py-0.5 font-mono text-[11px] dark:bg-white/10">
                              {ln.item_code}
                            </span>
                            <span>{ln.item_name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono">
                          {formatQty(ln.qty, ln.item_uom)}{" "}
                          <span className="opacity-60">{ln.item_uom}</span>
                        </td>
                        {delivery.kind === "external" && (
                          <>
                            <td className="px-3 py-1.5 text-right font-mono opacity-90">
                              {ln.unit_price ?? "—"}
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono">
                              {ln.line_total ?? "—"}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {delivery.notes && (
                <p className="border-t border-[var(--border)] bg-black/[0.02] px-3 py-1.5 text-xs italic opacity-80 dark:bg-white/[0.02]">
                  {delivery.notes}
                </p>
              )}
              {delivery.order_id && (
                <LinkedOrderLines orderId={delivery.order_id} orderCode={delivery.order_code} />
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function LinkedOrderLines({
  orderId,
  orderCode,
}: {
  orderId: string;
  orderCode: string | null;
}) {
  const orderQ = useOrder(orderId);
  const order = orderQ.data;
  return (
    <div className="border-t border-[var(--border)] bg-blue-50/40 px-3 py-2 text-xs dark:bg-blue-950/20">
      <div className="mb-1 flex items-center gap-1.5 font-medium opacity-80">
        <ShoppingCart className="size-3.5" aria-hidden />
        <Link
          to="/erp/orders/$orderId"
          params={{ orderId }}
          className="font-mono text-[var(--accent)] hover:underline"
        >
          {orderCode ?? "Order"}
        </Link>
        <span className="opacity-60">— ordered items</span>
      </div>
      {orderQ.isLoading ? (
        <p className="opacity-60">Loading order…</p>
      ) : !order ? (
        <p className="opacity-60">Order unavailable.</p>
      ) : (
        <ul className="space-y-0.5">
          {order.lines.map((ln) => (
            <li key={ln.id} className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5">
                {ln.is_custom && (
                  <span className="rounded bg-amber-100 px-1 py-0.5 text-[9px] font-medium text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
                    custom
                  </span>
                )}
                <span>{ln.item_name ?? "—"}</span>
                {ln.item_code && <span className="font-mono opacity-50">{ln.item_code}</span>}
              </span>
              <span className="font-mono tabular-nums opacity-80">
                {formatQty(ln.qty, ln.item_uom)}
                {ln.item_uom ? ` ${ln.item_uom}` : ""}
                {ln.unit_price ? ` · ₹${ln.unit_price}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KindPill({ kind }: { kind: "external" | "internal" }) {
  if (kind === "external") {
    return (
      <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
        External
      </span>
    );
  }
  return (
    <span className="rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-purple-800 dark:bg-purple-900/40 dark:text-purple-200">
      Internal
    </span>
  );
}

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  confirmColor,
  onCancel,
  onConfirm,
  loading,
}: {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  confirmColor: "accent" | "red" | "emerald" | "amber" | "blue";
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const colorClass = {
    accent: "bg-[var(--accent)] text-white hover:opacity-90",
    red: "bg-red-600 text-white hover:bg-red-500",
    emerald: "bg-emerald-600 text-white hover:bg-emerald-500",
    amber: "bg-amber-600 text-white hover:bg-amber-500",
    blue: "bg-blue-600 text-white hover:bg-blue-500",
  }[confirmColor];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      // biome-ignore lint/a11y/useSemanticElements: <dialog> doesn't compose with our backdrop-click pattern.
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default bg-black/40"
        onClick={onCancel}
      />
      <div className="relative w-full max-w-md space-y-4 rounded-xl border border-[var(--border)] bg-[var(--bg)] p-5 shadow-lg">
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        {body}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="size-4" aria-hidden /> Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50 ${colorClass}`}
          >
            {loading ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Check className="size-4" aria-hidden />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function CancelDialog({
  dispatch,
  onCancel,
  onConfirm,
  loading,
}: {
  dispatch: ErpDispatchRead;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  loading: boolean;
}) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onConfirm(reason.trim());
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      // biome-ignore lint/a11y/useSemanticElements: <dialog> doesn't compose with our backdrop-click pattern.
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default bg-black/40"
        onClick={onCancel}
      />
      <form
        onSubmit={onSubmit}
        className="relative w-full max-w-md space-y-4 rounded-xl border border-[var(--border)] bg-[var(--bg)] p-5 shadow-lg"
      >
        <h2 className="text-lg font-semibold tracking-tight">Cancel {dispatch.code}?</h2>
        <ReversalSummary dispatch={dispatch} />
        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">Reason (optional)</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="e.g. customer cancelled, wrong items picked, …"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="size-4" aria-hidden /> Keep dispatch
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Ban className="size-4" aria-hidden />
            )}
            Cancel dispatch
          </button>
        </div>
      </form>
    </div>
  );
}

function ReversalSummary({ dispatch }: { dispatch: ErpDispatchRead }) {
  if (dispatch.status === "draft") {
    return (
      <p className="text-sm opacity-80">
        Cancelling a draft has no inventory effect — nothing was deducted yet.
      </p>
    );
  }
  if (dispatch.status === "delivered") {
    return (
      <p className="text-sm opacity-80">
        This re-credits the source warehouses for every line, then debits any internal-delivery
        destinations (since their credit happened at delivered). The destination debit may be
        blocked if a destination warehouse is already at qty &lt; 0 for one of the items.
      </p>
    );
  }
  return (
    <p className="text-sm opacity-80">
      This re-credits the source warehouses for every line, restoring the inventory motion that
      happened at confirm.
    </p>
  );
}

function EditHeaderDialog({
  dispatch,
  onClose,
  onSaved,
  onError,
}: {
  dispatch: ErpDispatchRead;
  onClose: () => void;
  onSaved: () => void;
  onError: (e: unknown) => void;
}) {
  const queryClient = useQueryClient();
  const driversQ = useDrivers();
  const transportsQ = useTransports();
  const accountsQ = useQuery({
    queryKey: ["accounts", { limit: 200 }],
    queryFn: () => accountsApi.list({ limit: 200 }),
  });
  const contactsQ = useQuery({
    queryKey: ["contacts", { limit: 200, offset: 0 }],
    queryFn: () => contactsApi.list({ limit: 200, offset: 0 }),
  });
  const dealsQ = useQuery({
    queryKey: ["deals", { limit: 200 }],
    queryFn: () => dealsApi.list({ limit: 200 }),
  });

  const [driverId, setDriverId] = useState(dispatch.driver_id ?? "");
  const [tempDriverName, setTempDriverName] = useState(dispatch.temp_driver_name ?? "");
  const [transportId, setTransportId] = useState(dispatch.transport_id ?? "");
  const [tempTransportName, setTempTransportName] = useState(dispatch.temp_transport_name ?? "");
  const [accountId, setAccountId] = useState(dispatch.account_id ?? "");
  const [contactId, setContactId] = useState(dispatch.contact_id ?? "");
  const [dealId, setDealId] = useState(dispatch.deal_id ?? "");
  const [notes, setNotes] = useState(dispatch.notes ?? "");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const contactsForPicker = useMemo(() => {
    const items = contactsQ.data?.items ?? [];
    if (!accountId) return items;
    return items.filter((c) => c.account_id === accountId || c.id === contactId);
  }, [contactsQ.data?.items, accountId, contactId]);

  const mutation = useMutation({
    mutationFn: (body: ErpDispatchUpdate) => erpApi.dispatches.update(dispatch.id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["erp", "dispatches"] });
      toast.success("Dispatch updated.");
      onSaved();
    },
    onError,
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body: ErpDispatchUpdate = {};
    const cur = {
      driver_id: dispatch.driver_id ?? "",
      transport_id: dispatch.transport_id ?? "",
      temp_driver_name: dispatch.temp_driver_name ?? "",
      temp_transport_name: dispatch.temp_transport_name ?? "",
      account_id: dispatch.account_id ?? "",
      contact_id: dispatch.contact_id ?? "",
      deal_id: dispatch.deal_id ?? "",
      notes: dispatch.notes ?? "",
    };
    if (driverId !== cur.driver_id) body.driver_id = driverId || null;
    if (transportId !== cur.transport_id) body.transport_id = transportId || null;
    if (tempDriverName.trim() !== cur.temp_driver_name) {
      body.temp_driver_name = tempDriverName.trim() || null;
    }
    if (tempTransportName.trim() !== cur.temp_transport_name) {
      body.temp_transport_name = tempTransportName.trim() || null;
    }
    if (accountId !== cur.account_id) body.account_id = accountId || null;
    if (contactId !== cur.contact_id) body.contact_id = contactId || null;
    if (dealId !== cur.deal_id) body.deal_id = dealId || null;
    if (notes.trim() !== cur.notes) body.notes = notes.trim() || null;

    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    mutation.mutate(body);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      // biome-ignore lint/a11y/useSemanticElements: <dialog> doesn't compose with our backdrop-click pattern.
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default bg-black/40"
        onClick={onClose}
      />
      <form
        onSubmit={onSubmit}
        className="relative w-full max-w-2xl space-y-3 rounded-xl border border-[var(--border)] bg-[var(--bg)] p-5 shadow-lg"
      >
        <h2 className="text-lg font-semibold tracking-tight">Edit {dispatch.code}</h2>
        <p className="text-xs opacity-60">
          Driver / transport / CRM links / notes are editable until cancelled. Lines and recipients
          freeze after confirmation — to change them, cancel and create a new dispatch.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block space-y-1">
            <span className="text-xs font-medium opacity-80">Driver (master)</span>
            <select
              value={driverId}
              onChange={(e) => {
                setDriverId(e.target.value);
                if (e.target.value) setTempDriverName("");
              }}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
            >
              <option value="">— none —</option>
              {(driversQ.data?.items ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.code} — {d.name}
                  {!d.is_active ? " (inactive)" : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium opacity-80">…or one-off driver name</span>
            <input
              type="text"
              value={tempDriverName}
              onChange={(e) => {
                setTempDriverName(e.target.value);
                if (e.target.value) setDriverId("");
              }}
              maxLength={200}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium opacity-80">Transport (master)</span>
            <select
              value={transportId}
              onChange={(e) => {
                setTransportId(e.target.value);
                if (e.target.value) setTempTransportName("");
              }}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
            >
              <option value="">— none —</option>
              {(transportsQ.data?.items ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} — {t.vehicle_number}
                  {!t.is_active ? " (inactive)" : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium opacity-80">…or one-off vehicle</span>
            <input
              type="text"
              value={tempTransportName}
              onChange={(e) => {
                setTempTransportName(e.target.value);
                if (e.target.value) setTransportId("");
              }}
              maxLength={200}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
            />
          </label>

          <label className="block space-y-1">
            <span className="text-xs font-medium opacity-80">Linked deal</span>
            <select
              value={dealId}
              onChange={(e) => setDealId(e.target.value)}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
            >
              <option value="">— none —</option>
              {(dealsQ.data?.items ?? []).map((deal) => (
                <option key={deal.id} value={deal.id}>
                  {deal.stage} {deal.value ? `· ${deal.value}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium opacity-80">Linked account</span>
            <select
              value={accountId}
              onChange={(e) => {
                setAccountId(e.target.value);
                if (
                  contactId &&
                  (contactsQ.data?.items ?? []).find((c) => c.id === contactId)?.account_id !==
                    e.target.value
                ) {
                  setContactId("");
                }
              }}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
            >
              <option value="">— none —</option>
              {(accountsQ.data?.items ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1 sm:col-span-2">
            <span className="text-xs font-medium opacity-80">Linked contact</span>
            <select
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
            >
              <option value="">— none —</option>
              {contactsForPicker.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.phone ? `· ${c.phone}` : ""}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block space-y-1">
          <span className="text-xs font-medium opacity-80">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            maxLength={2000}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          />
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
          >
            <X className="size-4" aria-hidden /> Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-[var(--accent)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Edit className="size-4" aria-hidden />
            )}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}
